import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { buildSearchText, normalizeSearchValue, paginate } from "../../utils/directoryUtils";

export default function useDirectoryFilters({ records, filterKeys, pageSize = 12, initialFilters = {} }) {
  const [searchParams, setSearchParams] = useSearchParams();
  // The URL is the source of truth, including navigation back to a saved search.
  const query = searchParams.get("q") || "";
  const filters = Object.fromEntries(
    filterKeys.map((key) => [key, searchParams.get(key) || initialFilters[key] || ""]),
  );
  const requestedPage = Number(searchParams.get("page") || 1);
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;

  const filtered = useMemo(() => {
    const normalizedQuery = normalizeSearchValue(query);
    return records.filter((record) => {
      if (normalizedQuery && !buildSearchText(record).includes(normalizedQuery)) return false;
      return filterKeys.every((key) => {
        const selected = filters[key];
        if (key === "sort" || !selected) return true;
        const value = record[key];
        return Array.isArray(value) ? value.includes(selected) : String(value) === selected;
      });
    });
  }, [records, query, filters, filterKeys]);

  const sorted = useMemo(() => {
    const sort = filters.sort || "Alphabetical";
    return [...filtered].sort((a, b) => {
      if (sort === "Recently verified") return String(b.lastVerified).localeCompare(String(a.lastVerified));
      return String(a.name).localeCompare(String(b.name));
    });
  }, [filtered, filters.sort]);

  const pagination = paginate(sorted, page, pageSize);

  const updateSearch = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    next.delete("page");
    setSearchParams(next, { replace: true });
  };
  const setQuery = (value) => updateSearch("q", value);
  const updateFilter = (key, value) => updateSearch(key, value);
  const reset = () => {
    setSearchParams({}, { replace: true });
  };
  const setPage = (nextPage) => {
    const next = new URLSearchParams(searchParams);
    const safePage = Math.min(Math.max(Math.floor(Number(nextPage) || 1), 1), pagination.totalPages);
    if (safePage <= 1) next.delete("page"); else next.set("page", String(safePage));
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return { query, setQuery, filters, updateFilter, reset, filtered: sorted, pagination, setPage, hasQueryParams: Boolean(searchParams.toString()) };
}
